// src/pages/Customers/Customers.tsx
import React, { useEffect, useRef, useState } from "react";
import Table, { Column } from "../../components/Table";
import Modal from "../../components/Modal";
import FormBuilder, { FieldConfig } from "../../components/FormBuilder";
import { getProfiles, addProfile, updateProfile, deleteProfile } from "../../services/profileService";
import { mapProfileToRow, flattenProfile, TableRow } from "../../services/mappers";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Button from "../../components/Button";
import { useAuth } from "../../context/AuthContext";
import { customerFields } from "./CusFormFields";
import InputField from "../../components/InputField";
import { Icon } from "@iconify/react";
import axiosClient from "../../api/axiosClient";

export default function Customers() {
  const [data, setData] = useState<TableRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [openModal, setOpenModal] = useState(false);
  const [formErrors, setFormErrors] = useState<Record<string, string[]> | null>(null);
  const formRef = useRef<any>(null);
  const navigate = useNavigate();
  const [editingCustomer, setEditingCustomer] = useState<TableRow | null>(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [customerToDelete, setCustomerToDelete] = useState<TableRow | null>(null);
  const [deleting, setDeleting] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === "Admin";

  // Search + filter state (new)
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const debounceRef = useRef<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");

  // debounce searchTerm -> debouncedSearch after 500ms
  useEffect(() => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 500);
    return () => {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
    };
  }, [searchTerm]);

  // immediate apply on Enter
  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      if (debounceRef.current) window.clearTimeout(debounceRef.current);
      setDebouncedSearch(searchTerm.trim());
    }
  };

  // centralised fetch that uses /profiles/search when needed
  const fetchProfiles = async () => {
    setLoading(true);
    try {
      const useSearch = (debouncedSearch && debouncedSearch.length > 0) || (statusFilter && statusFilter !== "all");

      if (useSearch) {
        const params: any = {};
        if (debouncedSearch) params.query = debouncedSearch;
        if (statusFilter && statusFilter !== "all") params.status = statusFilter;
        const res = await axiosClient.get("/profiles/search", { params });
        const payload = res.data ?? {};
        const rawProfiles = Array.isArray(payload.profiles)
          ? payload.profiles
          : Array.isArray(payload.data)
          ? payload.data
          : Array.isArray(payload)
          ? payload
          : [];
        const rows = rawProfiles.map(mapProfileToRow);
        // keep only customers
        setData(rows.filter((r:any) => r.role === "Customer"));
      } else {
        // fallback to existing getProfiles()
        const res = await getProfiles();
        const rows = (res.profiles || []).map(mapProfileToRow);
        const customers = rows.filter((r) => r.role === "Customer");
        setData(customers);
      }
      setError(null);
    } catch (err: any) {
      console.error("Failed to fetch customers", err);
      setData([]);
      setError(err?.message ?? "Failed to load customers");
      toast.error(err?.message || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  };

  // load on mount and whenever debouncedSearch or statusFilter change
  useEffect(() => {
    fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, statusFilter]);

  // keep an alias for manual reload
  const load = async () => {
    await fetchProfiles();
  };

  useEffect(() => {
    // initial load already triggered by effect but keep safety
    // fetchProfiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: Column<TableRow>[] = [
    {
      header: "Customer",
      accessor: "name",
      sortable: true,
      cell: (row) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            {row.profile ? (
              <img src={row.profile} alt={row.name} className="w-10 h-10 rounded-full object-cover" />
            ) : (
              <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500">
                <span className="text-white font-semibold">{row.name?.charAt(0)?.toUpperCase()}</span>
              </div>
            )}

            {(row.raw.status === "Active" || row.raw.status === "Inactive") && (
              <span
                className={`absolute top-0 right-0 w-3 h-3 border-2 border-white rounded-full ${
                  row.raw.status === "Active" ? "bg-green-500" : "bg-red-500"
                }`}
              ></span>
            )}
          </div>
          <div>
            <div className="font-medium">{row.name}</div>
            {row.email && <div className="text-xs text-gray-400">{row.email}</div>}
          </div>
        </div>
      ),
    },
    {
      header: "Mobile",
      accessor: "mobile_number",
      cell: (row) =>
        row.mobile_number ? (
          <a href={`tel:${row.mobile_number}`} className="text-sm hover:underline">
            {row.mobile_number}
          </a>
        ) : (
          <span className="text-sm text-gray-400">—</span>
        ),
      width: "w-40",
    },
    {
      header: "Code",
      accessor: "id",
      width: "w-28",
      cell: (row) => row.raw?.customer_code ?? <span className="text-sm text-gray-400">—</span>,
    },
  ];

// ------------------------------
// Shared FormData Helper
// ---------- Replace your current objectToFormData with this ----------
const isFileLike = (v: any) =>
  v instanceof File ||
  (typeof v === "object" && v !== null && (v instanceof Blob || (typeof v.size === "number" && typeof v.type === "string" && typeof v.name === "string")));

const normalizeToFile = (val: any): File | undefined => {
  if (!val) return undefined;

  // FileList (from <input type="file" />)
  if (val instanceof FileList && val.length > 0) return val[0];

  // Direct File / Blob
  if (isFileLike(val)) return val as File;

  // Array of file-likes e.g. [File], react-dropzone or FormBuilder that returns an array
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (isFileLike(first)) return first as File;
    // antd UploadFile shape: { originFileObj: File }
    if (first?.originFileObj && isFileLike(first.originFileObj)) return first.originFileObj;
    // some libs: { file: File }
    if (first?.file && isFileLike(first.file)) return first.file;
  }

  // single object wrapper: { originFileObj: File } or { file: File }
  if (val?.originFileObj && isFileLike(val.originFileObj)) return val.originFileObj;
  if (val?.file && isFileLike(val.file)) return val.file;

  return undefined;
};

const objectToFormData = (obj: any) => {
  if (obj instanceof FormData) return obj;
  const fd = new FormData();

  Object.entries(obj || {}).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    // If value resolves to a File (or wrapped File), append it directly
    const maybeFile = normalizeToFile(value);
    if (maybeFile) {
      fd.append(key, maybeFile);
      return;
    }

    // If it's a FileList (covered above), handled; arrays with non-files -> stringify
    if (Array.isArray(value)) {
      fd.append(key, JSON.stringify(value));
      return;
    }

    // Plain object -> stringify
    if (typeof value === "object") {
      fd.append(key, JSON.stringify(value));
      return;
    }

    // Primitive -> string
    fd.append(key, String(value));
  });

  return fd;
};
// -------------------------------------------------------------------


// ------------------------------
// ADD NEW CUSTOMER
// ------------------------------
const handleAddCustomer = async (formData: any) => {
  setFormErrors(null);
  // console.log('profile_photo (raw):', formData.profile_photo);

  
  try {
    // Defaults
    if (!formData.role) formData.role = "Customer";

    // Customer Code logic
    if (formData.customer_code_option === "Generate") {
      delete formData.customer_code;
    } else if (formData.customer_code_option === "Enter") {
      if (formData.customer_code && !formData.customer_code.startsWith("CUS")) {
        formData.customer_code = `CUS${formData.customer_code}`;
      }
    }

    const fd = objectToFormData(formData);
    
    const res = await addProfile(fd);
    

    toast.success(res.message || "Customer added successfully!");
    setOpenModal(false);
    await load();
    formRef.current?.reset();
  } catch (err: any) {
    const errorData = err?.response?.data;
    if (errorData) {
      setFormErrors(errorData.errors ?? null);
      toast.error(
        [errorData.message, ...(Object.values(errorData.errors || {}).flat())]
          .filter(Boolean)
          .join(", ")
      );
    } else {
      toast.error(err?.message || "Failed to add customer");
    }
    console.error(err);
  }
};

// ------------------------------
// UPDATE CUSTOMER
// ------------------------------
const handleUpdateCustomer = async (formData: any) => {
  if (!editingCustomer) return;
  setFormErrors(null);

  const allowedFields = [
    "email",
    "mobile_number",
    "first_name",
    "last_name",
    "date_of_birth",
    "gender",
    "marital_status",
    "father_name",
    "mother_name",
    "spouse_name",
    "aadhaar_number",
    "pan_number",
    "driving_license",
    "voter_id",
    "passport_number",
    "salary",
    "employment_type",
    "employer_name",
    "work_experience",
    "credit_score",
    "monthly_income",
    "other_liabilities",
    "status",
    "profile_photo",
    "sign_photo",
  ];

  try {
    const id = editingCustomer.raw?.profile_id ?? editingCustomer.id;

    // Filter only update fields
    const filtered: any = {};
    Object.entries(formData).forEach(([k, v]) => {
      if (allowedFields.includes(k)) {
        filtered[k] = v;
      }
    });

    const fd = objectToFormData(filtered);
    const res = await updateProfile(id, fd);

    toast.success(res.message || "Customer updated successfully!");
    setOpenModal(false);
    setEditingCustomer(null);
    await load();
  } catch (err: any) {
    const errorData = err?.response?.data;
    if (errorData) {
      setFormErrors(errorData.errors ?? null);
      toast.error(
        [errorData.message, ...(Object.values(errorData.errors || {}).flat())]
          .filter(Boolean)
          .join(", ")
      );
    } else {
      toast.error(err?.message || "Failed to update customer");
    }
    console.error(err);
  }
};


  const handleDeleteClick = (row: TableRow) => {
    setCustomerToDelete(row);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!customerToDelete) return;
    setDeleting(true);
    try {
      const id = customerToDelete.raw?.profile_id ?? customerToDelete.id;
      const res = await deleteProfile(id);
      toast.success(res.message || "Customer deleted successfully!");
      setDeleteModalOpen(false);
      setCustomerToDelete(null);
      await load();
    } catch (res: any) {
      console.error(res);
      toast.error(res?.response?.data?.message || "Failed to delete customer");
    } finally {
      setDeleting(false);
    }
  };

  // ensure username is readOnly on edit
  const getCustomerFields = (isEdit: boolean) => {
    const filtered = customerFields
      .filter((f) => {
        if (!("name" in f)) return true;
        if (isEdit && ["password", "password_confirmation", "customer_code_option", "customer_code"].includes(f.name)) {
          return false;
        }
        return true;
      })
      .map((f) => {
        if (!("name" in f)) return f;
        if (isEdit && f.name === "username") return { ...f, readOnly: true };
        return f;
      });

    const cleaned: FieldConfig[] = [];
    for (let i = 0; i < filtered.length; i++) {
      const item = filtered[i];
      if (!("name" in item)) {
        const next = filtered[i + 1];
        if (!next || !("name" in next)) continue;
      }
      cleaned.push(item);
    }
    return cleaned;
  };

  const buildDefaultValues = (fields: FieldConfig[]) => {
    const defaults: any = {};
    fields.forEach((field) => {
      if ("name" in field && field.defaultValue !== undefined) {
        defaults[field.name] = field.defaultValue;
      }
    });
    return defaults;
  };

  return (
    <div className="pt-3">
      <div className="grid grid-cols-1 md:grid-cols-2 mb-4 gap-4 items-center bg-white p-4 rounded-xl shadow-sm">
        <div className="flex justify-between">
          <h2 className="text-2xl font-semibold">Customer</h2>
          {/* Filter Dropdown (wired) */}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-gray-200 shadow-sm rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-yellow-300 focus:outline-none transition"
          >
            <option value="all">All</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>

        <div className="">
          {/* Search Input (wired) */}
          <div className="relative w-full ">
            <InputField
              placeholder="Search (name, code, etc...)"
              className="w-full focus:outline-none focus:ring-2 focus:ring-sky-300 transition"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={handleSearchKeyDown}
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
              <Icon icon="mdi:magnify" width={18} className="text-slate-400" />
            </div>
          </div>
        </div>
      </div>

      <Table<TableRow>
        // title="Customers"
        data={data}
        columns={columns}
        loading={loading}
        // showActions={isAdmin}
        defaultPageSize={10}
        pageSizeOptions={[5, 10, 25]}
        onRowClick={(row) => navigate("/customers/details", { state: { id: row.id } })}
        onAdd={() => {
          setEditingCustomer(null);
          setOpenModal(true);
        }}
        // onEdit={async (row) => {
        //   const maybeProfile = row.raw ?? row;
        //   const flattened = maybeProfile.profile_id ? flattenProfile(maybeProfile) : maybeProfile;
        //   setEditingCustomer({ ...row, raw: flattened });
        //   setOpenModal(true);
        // }}
        // onDelete={handleDeleteClick}
      />

      {/* Add / Edit Modal */}
      <Modal isOpen={openModal} onClose={() => setOpenModal(false)} title={editingCustomer ? "Edit Customer" : "Add Customer"}>
        <FormBuilder
          ref={formRef}
          fields={getCustomerFields(!!editingCustomer)}
          onSubmit={editingCustomer ? handleUpdateCustomer : handleAddCustomer}
          onCancel={() => setOpenModal(false)}
          submitLabel={editingCustomer ? "Update Customer" : "Add Customer"}
          error={formErrors}
          defaultValues={editingCustomer ? editingCustomer?.raw : buildDefaultValues(getCustomerFields(!!editingCustomer))}
        />
      </Modal>

      {/* Delete Modal */}
      <Modal isOpen={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} title="Confirm Delete">
        <p className="mb-5 text-gray-700">
          Are you sure you want to delete <span className="font-semibold">{customerToDelete?.name}</span>?
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setDeleteModalOpen(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmDelete} disabled={deleting}>
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </div>
      </Modal>
    </div>
  );
}
